import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "./user-menu.css";

interface UserMenuProps {
  givenName: string;
  familyName: string;
  email: string;
  onSignOut: () => void;
}

function computeInitials(givenName: string, familyName: string, email: string): string {
  const first = givenName?.trim()[0];
  const last = familyName?.trim()[0];
  if (first && last) return `${first}${last}`.toUpperCase();
  if (first) return first.toUpperCase();
  const emailPart = email.split("@")[0] || "";
  return emailPart.slice(0, 2).toUpperCase();
}

export function UserMenu({ givenName, familyName, email, onSignOut }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const initials = computeInitials(givenName, familyName, email);
  const fullName = [givenName, familyName].filter(Boolean).join(" ").trim();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;

    const handleClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        close();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("mousedown", handleClick);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("keydown", handleKey);
    };
  }, [open, close]);

  return (
    <div className="user-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className="user-menu-avatar"
        aria-label="Open user menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {initials}
      </button>

      {open && (
        <div className="user-menu-pop" role="menu">
          <div className="user-menu-header">
            {fullName && <div className="user-menu-name">{fullName}</div>}
            <div className="user-menu-email">{email}</div>
          </div>
          <div className="user-menu-divider" />
          <Link
            to="/profile"
            className="user-menu-item"
            role="menuitem"
            onClick={close}
          >
            Profile
          </Link>
          <button
            type="button"
            className="user-menu-item user-menu-signout"
            role="menuitem"
            onClick={() => {
              close();
              onSignOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
